import { FC, HTMLAttributes, useEffect } from "react";
import styled from "styled-components";
import { animated, useSpring, easings, config } from "@react-spring/web";
import { reverseWobbly } from "utils/constants";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { CardType } from "types";

const Front = styled(animated.div)<{ $value: number }>`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: ${props => ((props.$value - 1) % 13) * -74}px 0;

  &.spade {
    background-image: url(images/spade.png);
  }

  &.diamond {
    background-image: url(images/diamond.png);
  }

  &.club {
    background-image: url(images/club.png);
  }

  &.heart {
    background-image: url(images/heart.png);
  }
`;

const Container = styled.div`
  width: 74px;
  height: 112px;
  position: relative;
  grid-row: 2 / 3;
`;

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  index: number,
  card: CardType,
  hideState: number,
  skip: boolean;
  onShow: () => void,
  onHide?: () => void,
};

export const Card: FC<CardProps> = ({ index, card, hideState, skip, onShow, onHide, style, ...props }) => {
  const suit = card.split('_').shift();
  const value = Number(card.split('_').pop());
  const play = useSoundStore(store => store.play);

  const [spring, api] = useSpring(() => ({
    transform: 'scale(0)',
    opacity: 1
  }));

  useEffect(() => {
    if(!hideState){
      api.start({
        transform: `scale(1)`,
        delay: 250 * index,
        config: config.wobbly,
        onRest: onShow,
        onStart: () => {
          play(Sounds.CardReveal);
        }
      });
    }else{
      if(hideState === 2){
        api.start({
          transform: `scale(0)`,
          delay: 250 * index,
          config: reverseWobbly,
          onRest: onHide
        });
      }else{
        api.start({
          opacity: 0,
          delay: hideState > 2 ? 500 : 0,
          config: { 
            easing: easings.easeOutSine,
            duration: hideState === 4 ? 250 : 1000,
          },
          onRest: onHide
        });
      }
    }
  }, [api, hideState, index, onHide, onShow, play]);

  useEffect(() => {
    if(skip) api.stop();
  }, [skip, api]);

  return (
    <Container style={style}>
      <Front className={suit} $value={value} style={spring} />  
    </Container>
  );
};